import React, { useCallback, useEffect, useRef } from 'react'

/** 다이얼로 줄 수 있는 각도 범위(도) */
export const ROTATION_MAX = 45

/** 1°당 눈금 간격(px) */
const PX_PER_DEG = 6

/** 이 각도 안이면 0°로 붙인다 — 수평으로 되돌리기 쉽게 */
const SNAP_DEG = 0.7

interface RotationDialProps {
  /** 현재 각도(도) */
  value: number
  onChange: (deg: number) => void
  /** 끌기가 끝났을 때 (한 번만 무거운 일을 하고 싶을 때 쓴다) */
  onChangeEnd?: () => void
}

/**
 * 아이폰 사진 편집의 각도 눈금.
 *
 * 좌우로 밀면 각도가 바뀐다. 오른쪽으로 밀면 사진이 왼쪽으로 도는데,
 * 이건 눈금이 고정된 지시선 아래로 지나간다고 보면 자연스럽다(아이폰과 같다).
 *
 * canvas로 그리는 이유
 * ------------------
 * 눈금이 90개가 넘고 끌 때마다 다시 그린다. DOM 요소 90개를 매 프레임
 * 옮기면 저사양 기기에서 버벅인다.
 */
const RotationDial: React.FC<RotationDialProps> = ({
  value,
  onChange,
  onChangeEnd,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const draggingRef = useRef(false)
  const lastXRef = useRef(0)
  const valueRef = useRef(value)

  /*
   * 손가락이 실제로 움직인 각도. 0° 흡착과 분리해서 들고 있는다.
   *
   * 예전에는 흡착된 값(0)을 그대로 다음 계산의 기준으로 썼다. 그러면 0 근처에서
   * 매 프레임 기준이 0으로 되돌아가, 천천히 밀면 0.3° 갔다가 0, 다시 0.5° 갔다가
   * 0이 반복된다. 0을 빠져나가려면 한 프레임에 흡착 범위(0.7° ≈ 4px)를 넘게
   * 움직여야 해서, 0에서 뻑뻑하게 걸리고 양수↔음수로 넘어갈 때 멈춘 것처럼 보였다.
   *
   * 실제 각도를 따로 누적하면 0 구간에서도 손가락을 그대로 따라가고, 범위를
   * 벗어나는 순간 자연스럽게 풀린다. 화면에 보여주고 저장하는 값만 0으로 붙는다.
   */
  const rawRef = useRef(value)

  useEffect(() => {
    valueRef.current = value
    // 밖에서 값이 바뀌면(초기화 등) 실제 각도도 맞춰준다.
    // 끌고 있는 중에는 건드리지 않는다 — 흡착된 0이 실제 각도를 덮어쓰면 안 된다.
    if (!draggingRef.current) rawRef.current = value
  }, [value])

  const draw = useCallback(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas) return
    const w = wrap.clientWidth
    const h = wrap.clientHeight
    if (w === 0 || h === 0) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = w * dpr
    canvas.height = h * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)

    const cx = w / 2
    const base = h - 12
    const deg = valueRef.current

    for (let d = -ROTATION_MAX; d <= ROTATION_MAX; d++) {
      const x = cx + (d - deg) * PX_PER_DEG
      if (x < -20 || x > w + 20) continue
      const isMajor = d % 15 === 0
      const isMid = d % 5 === 0
      const len = isMajor ? 16 : isMid ? 11 : 6
      // 가운데서 멀수록 흐리게
      const fade = Math.max(0, 1 - Math.abs(x - cx) / (w / 2))
      ctx.strokeStyle = `rgba(75, 54, 33, ${0.15 + 0.5 * fade})`
      ctx.lineWidth = isMajor ? 1.6 : 1
      ctx.beginPath()
      ctx.moveTo(x, base)
      ctx.lineTo(x, base - len)
      ctx.stroke()
      if (isMajor) {
        ctx.fillStyle = `rgba(75, 54, 33, ${0.25 + 0.45 * fade})`
        ctx.font = '600 10px Pretendard, sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(String(d), x, base - 20)
      }
    }

    // 가운데 지시선
    ctx.strokeStyle = '#6B5E46'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(cx, base + 4)
    ctx.lineTo(cx, base - 20)
    ctx.stroke()
  }, [])

  useEffect(() => {
    draw()
  }, [draw, value])

  useEffect(() => {
    window.addEventListener('resize', draw)
    return () => window.removeEventListener('resize', draw)
  }, [draw])

  const handleDown = (e: React.PointerEvent) => {
    draggingRef.current = true
    lastXRef.current = e.clientX
    // 끌기를 시작할 때 실제 각도를 지금 값에서 다시 잡는다
    rawRef.current = valueRef.current
    wrapRef.current?.setPointerCapture(e.pointerId)
  }

  const handleMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    // 실제 각도를 누적한다. 흡착은 보여줄 때만 적용한다
    let raw = rawRef.current - (e.clientX - lastXRef.current) / PX_PER_DEG
    lastXRef.current = e.clientX
    raw = Math.max(-ROTATION_MAX, Math.min(ROTATION_MAX, raw))
    rawRef.current = raw

    // 끄는 중에는 흡착하지 않는다.
    // 끌면서 0으로 붙이면 0~0.7° 사이 값이 화면에 아예 안 나와서
    // 0.8 다음이 곧바로 0이 된다. 그 구간이 비면 아무리 부드럽게 계산해도
    // 눈에는 툭 끊겨 보인다. 흡착은 손을 뗄 때 한 번만 한다.
    onChange(raw)
  }

  const handleUp = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    // 소수 첫째 자리까지만 남긴다 — 표시와 저장이 같은 값이 되게
    // 손을 뗄 때 0 근처면 수평으로 붙인다 — 되돌리기 쉬우라고 두는 동작이다
    let settled = Math.round(valueRef.current * 10) / 10
    if (Math.abs(settled) < SNAP_DEG) settled = 0
    rawRef.current = settled
    onChange(settled)
    onChangeEnd?.()
  }

  return (
    <div className="w-full px-16px">
      <div className="w-full text-center font-pretendard font-bold text-13px text-[#6B5E46] tabular-nums">
        {Math.round(value * 10) / 10}°
      </div>
      <div
        ref={wrapRef}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        className="w-full h-66px relative overflow-hidden"
        style={{ touchAction: 'none' }}
      >
        <canvas ref={canvasRef} className="block w-full h-full" />
      </div>
    </div>
  )
}

export default RotationDial
